/**
 * ink-wet animation: M5.T3.
 *
 * Per IMPLEMENTATION_PLAN.md §7 M5.T3:
 *   "scale to 0.97 + background darken (paper → smudge) for 150ms, then
 *    back. Applied to all <ScribbleButton>s."
 *
 * Mechanism: on pointerdown, add `INK_WET_CLASS` to the target element.
 * The class triggers a 150ms keyframe animation that dips scale + filter
 * brightness then returns. The class is removed on `animationend` so a
 * subsequent click can re-fire the animation.
 *
 * The brightness darken is variant-agnostic (works on the --coral
 * primary background and the paper secondary). Background-color tweens
 * directly would need per-variant keyframes; `filter: brightness(0.92)`
 * approximates the paper → smudge dip while staying generic.
 *
 * Reduced motion: the `prefers-reduced-motion: reduce` media query in
 * `anim.css` short-circuits the animation to zero-duration so the class
 * still toggles (state remains observable) but no motion plays.
 */

import { useLayoutEffect, useRef } from "preact/hooks";
import type { RefObject } from "preact";

export const INK_WET_CLASS = "ink-wet-press";

/** Duration of the keyframe in ms. */
export const INK_WET_DURATION_MS = 150;

/**
 * Attach the ink-wet click animation to an element ref. Adds
 * `INK_WET_CLASS` on pointerdown and removes it on animationend (or
 * defensively after a 2× duration timeout if animationend never fires,
 * e.g. when the element is unmounted mid-animation).
 */
export function useInkWetClick<T extends HTMLElement>(ref: RefObject<T | null>): void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // useLayoutEffect so the listener is attached synchronously during
  // commit: keeps unit-tests deterministic when they fireEvent
  // immediately after render.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearPress = () => {
      el.classList.remove(INK_WET_CLASS);
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const onDown = () => {
      // Re-trigger by removing and re-adding on the next frame.
      el.classList.remove(INK_WET_CLASS);
      // Force a reflow so the re-added class re-fires the animation.
      void el.offsetWidth;
      el.classList.add(INK_WET_CLASS);
      // Defensive fallback in case animationend never fires.
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(clearPress, INK_WET_DURATION_MS * 2);
    };

    const onAnimEnd = (e: AnimationEvent) => {
      // Every button (primary included, since item 4 flattened it) runs the plain
      // `ink-wet` keyframe: match its name so an unrelated animationend bubbling
      // up (e.g. a page-tear) doesn't clear the press early.
      if (e.animationName === "ink-wet") clearPress();
    };

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("animationend", onAnimEnd);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("animationend", onAnimEnd);
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    };
  }, [ref]);
}
